/**
 * How a partner text actually leaves.
 *
 * Today there is exactly one channel and it is a person's thumb: Roof Board
 * builds an `sms:` link, iOS opens Messages with the recipient and the body
 * filled in, and the rep taps the blue arrow. Roof Board never sends an SMS
 * (ADR 0067).
 *
 * The adapter exists so that a later Twilio channel is a new case in one switch
 * rather than a search through the screens. It is deliberately *not* built:
 * adding it would mean revisiting the STOP footer, the consent wording and the
 * 10DLC registration, all of which exist to make an automated message lawful
 * and none of which apply to a person tapping Send.
 */

import { smsLink } from "@roof-hawk/core";

export type SendChannel = "phone" | "twilio";

/** The channel in use. One value, and the type is the point. */
export const SEND_CHANNEL: SendChannel = "phone";

export type SendOutcome =
  /** Messages was opened. Whether the rep then tapped Send is unknowable. */
  { state: "handed-off" } | { state: "refused"; reason: string };

/**
 * Open Messages with the text ready to go.
 *
 * `window.location.href` rather than `window.open`, and this is not a style
 * choice:
 *
 *   * `window.open` on iOS is popup-blocked from anything that is not a direct
 *     tap, and this is sometimes called from a promise continuation after the
 *     message has been marked sent.
 *   * Even when it is not blocked, it leaves an orphan tab behind Messages. The
 *     rep swipes back to Safari, lands on a blank tab, and the send queue —
 *     which has already advanced to the next card — is one tab over. Forty
 *     texts becomes forty confused tab switches.
 *
 * Navigating away from a page that is not going anywhere is safe: the `sms:`
 * scheme is handed to Messages by the operating system and the page stays put.
 */
export function openSms(phoneE164: string | null, body: string): SendOutcome {
  const link = smsLink(phoneE164, body);
  if (!link) {
    return {
      state: "refused",
      reason:
        "No mobile number on file — an office line cannot receive a text.",
    };
  }

  window.location.href = link;
  return { state: "handed-off" };
}

/**
 * Send by whichever channel is configured.
 *
 * The Twilio branch is a stated absence rather than a `TODO`: if somebody
 * flips `SEND_CHANNEL` without building it, they get a sentence saying so
 * rather than a button that silently does nothing.
 */
export function send(
  channel: SendChannel,
  phoneE164: string | null,
  body: string,
): SendOutcome {
  if (channel === "phone") return openSms(phoneE164, body);
  return {
    state: "refused",
    reason:
      "Twilio sending is not built. Texts go out from the rep's own phone " +
      "(ADR 0067).",
  };
}
